/**
 * Veterinary Clinic module-details request/response shapes.
 * Mirrors backend/src/modules/vet/vet.validators.ts.
 *
 * Decimal quirk: backend stores session_fee_inr as Prisma Decimal and
 * serializes it as a string in responses. The client treats it as a
 * string-shaped numeric (display + form input), and coerces to number on
 * POST since the request schema is z.number().
 */

export type MedicalDegree =
  | 'BVSC'
  | 'BVSC_AH'
  | 'MVSC'
  | 'PHD_VET'
  | 'OTHER';

export type PetSpecies = 'DOG' | 'CAT' | 'BIRD' | 'RABBIT' | 'FISH' | 'OTHER';

export type VetDetailsRequest = {
  vet_full_name: string; // 2..120
  medical_degree?: MedicalDegree;
  years_of_experience: number; // 0..80 integer
  registration_number: string; // 5..60 chars, alphanumeric + space/dash/slash
  session_fee_inr: number; // > 0, ≤ 100000
  pet_types_treated: PetSpecies[]; // 1..6 unique
  specialization_codes: string[]; // 1..10 unique lookup codes
};

export type VetDetailsResponse = {
  id: string;
  vendor_profile_id: string;
  vet_full_name: string;
  medical_degree: MedicalDegree | null;
  years_of_experience: number;
  registration_number: string;
  /** Prisma Decimal — backend returns as string (e.g. "750"). */
  session_fee_inr: string;
  pet_types_treated: PetSpecies[];
  specialization_codes: string[];
  created_at: string;
  updated_at: string;
  /**
   * True when this save changed the degree or registration number on an already
   * verified profile — KYC drops back to in-review until an admin re-verifies.
   * Only meaningful on the POST response.
   */
  reverification_required?: boolean;
};

export const REGISTRATION_NUMBER_REGEX = /^[A-Za-z0-9\s/-]+$/;

export const MEDICAL_DEGREE_OPTIONS: Array<{ value: MedicalDegree; label: string }> = [
  { value: 'BVSC', label: 'BVSc' },
  { value: 'BVSC_AH', label: 'BVSc & AH' },
  { value: 'MVSC', label: 'MVSc' },
  { value: 'PHD_VET', label: 'PhD (Veterinary)' },
  { value: 'OTHER', label: 'Other' },
];

export const PET_SPECIES_OPTIONS: Array<{ value: PetSpecies; label: string }> = [
  { value: 'DOG', label: 'Dog' },
  { value: 'CAT', label: 'Cat' },
  { value: 'BIRD', label: 'Bird' },
  { value: 'RABBIT', label: 'Rabbit' },
  { value: 'FISH', label: 'Fish' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * Hard-coded mirror of backend/prisma/seed.ts VET_SPECIALIZATIONS.
 * Wave 6 polish replaces this with GET /api/v1/lookups/vet-specializations.
 */
export const VET_SPECIALIZATION_OPTIONS: Array<{
  code: string;
  display_name: string;
}> = [
  { code: 'SURGERY', display_name: 'Surgery' },
  { code: 'DERMATOLOGY', display_name: 'Dermatology' },
  { code: 'ORTHOPEDICS', display_name: 'Orthopedics' },
  { code: 'NUTRITION', display_name: 'Nutrition' },
  { code: 'DENTAL_CARE', display_name: 'Dental Care' },
  { code: 'EMERGENCY_CARE', display_name: 'Emergency Care' },
  { code: 'INTERNAL_MEDICINE', display_name: 'Internal Medicine' },
  { code: 'EXOTIC_PETS', display_name: 'Exotic Pets' },
];
